import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { enqueueSnackbar } from "notistack";

import { Box } from "@mui/material";
import { BrokenImage } from "@mui/icons-material";

import { TrainReportSortColumn } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { DataExportButton } from "../../../../../../components/common/buttons/export-button";
import { ReportsTable } from "./table";
import { downloadCsv } from "../../../../../../utils/download-csv";
import { useToFakeUtcISO } from "../../../../../../hooks";
import { formatDateHumanReadable } from "../../../../../../utils";
import { useTrainTables } from "../hooks/use-train-tables";
import { IReportsValues, IReportsResponse } from "./interfaces";

export const Reports: FC<{ isActiveTableTab?: boolean }> = props => {
  const { isActiveTableTab } = props;

  const [reports, setReports] = useState<IReportsResponse[]>([]);

  const {
    trainId,
    startTimestamp,
    endTimestamp,
    isRecorderMode,
    recorderWindowStart,
    sortColumn,
    sortOrder,
    handleSort,
    canFetch,
  } = useTrainTables({
    isActiveTableTab,
    defaultSortColumn: TrainReportSortColumn.Date,
    onReset: () => setReports([]),
  });

  const { formatMessage } = useIntl();
  const toFakeUtcISO = useToFakeUtcISO();

  const reportsId = "pages.scheme.reports";
  const headersId = `${reportsId}.headers`;
  const noReports = formatMessage({ id: "pages.scheme.reports.empty" });

  const { fn: getReports, isLoading } = useApiCall(
    (api, { trainId, startTimestamp, endTimestamp, sortColumn, sortOrder }: IReportsValues) => {
      return api
        .fetchJson({
          url: `/report/${trainId}`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
            sortColumn,
            sortOrder,
          },
        })
        .then((json: IReportsResponse[] | null) => json ?? []);
    },
    { success: false },
  );

  const applyFakeUtcToReports = (items: IReportsResponse[]): IReportsResponse[] =>
    items.map(item => ({
      ...item,
      Date: toFakeUtcISO(item.Date),
    }));

  const handleExport = (): void => {
    try {
      if (!reports || !reports.length) {
        return;
      }

      if (trainId) {
        const date = toFakeUtcISO(new Date()).split("T")[0];
        const fileName = `train-reports-${trainId}-${date}.csv`;
        downloadCsv(
          reports.map(r => ({
            idReport: r.idReport,
            name: r.Name || "-",
            date: formatDateHumanReadable(r.Date),
            templateName: r.TemplateName || "-",
          })),
          fileName,
          [
            { field: "name", title: formatMessage({ id: `${headersId}.name` }) },
            { field: "date", title: formatMessage({ id: `${headersId}.date` }) },
            { field: "templateName", title: formatMessage({ id: `${headersId}.template` }) },
          ],
        );
      }
    } catch (error: unknown) {
      console.error("Error during export:", error);
      enqueueSnackbar(formatMessage({ id: "snackbar.exportError" }), { variant: "error" });
    }
  };

  useEffect(() => {
    if (!canFetch) return;

    void getReports(undefined, {
      trainId,
      startTimestamp,
      endTimestamp,
      ...(isRecorderMode ? {} : { sortColumn, sortOrder }),
    }).then(json => {
      if (!Array.isArray(json)) return;

      if (isRecorderMode) {
        setReports(prev => {
          const converted = applyFakeUtcToReports(json);
          const newItems = converted.filter(item => !prev.some(prevItem => prevItem.idReport === item.idReport));
          const updated = [...prev, ...newItems];
          const filtered = updated.filter(item => new Date(item.Date) >= new Date(recorderWindowStart));
          return filtered;
        });
      } else {
        setReports(applyFakeUtcToReports(json));
      }
    });
  }, [
    trainId,
    startTimestamp,
    endTimestamp,
    sortColumn,
    sortOrder,
    isRecorderMode,
    recorderWindowStart,
    isActiveTableTab,
  ]);

  return (
    <ProgressOverlay
      isLoading={isLoading}
      wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        {!reports.length ? (
          <EmptyPlaceholder icon={<BrokenImage />} text={noReports} />
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", height: "34px" }}>
              <DataExportButton handleExport={handleExport} />
            </Box>
            <ReportsTable reports={reports} handleSort={handleSort} sortColumn={sortColumn} sortOrder={sortOrder} />
          </>
        )}
      </Box>
    </ProgressOverlay>
  );
};
